import { Link } from "@tanstack/react-router";
import { Mail, Phone, MessageCircle, MapPin, Star, ExternalLink, Facebook, Instagram, Clock } from "lucide-react";
import { SITE } from "@/config/site";
import logoAssetUrl from "@/assets/izhub-logo-transparent.webp";
const logoAsset = { url: logoAssetUrl };


const areas = [
  "Tidworth",
  "Tidworth Garrison",
  "Perham Down",
  "Ludgershall",
  "Shipton Bellinger",
  "Collingbourne",
  "Bulford",
  "Larkhill",
  "Amesbury",
  "Andover",
  "Salisbury",
  "Salisbury Plain",
];

export function Footer() {
  return (
    <footer className="border-t border-border bg-cream/90 backdrop-blur-sm mt-24">
      <div className="container-x py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-5">
        <div>
          <Link to="/" className="inline-flex items-center rounded-2xl p-3" aria-label="IZHUB Cleaning Services home">
            <img
              src={logoAsset.url}
              alt="IZHUB Cleaning Services logo"
              className="h-16 w-auto object-contain"
              width={220}
              height={124}
              loading="lazy"
            />
          </Link>

          <p className="mt-4 text-sm text-muted-foreground max-w-xs">
            Professional, insured cleaning services across Tidworth, Andover, Salisbury and the wider Salisbury Plain area.
          </p>
        </div>

        <div>
          <h2 className="font-display text-base mb-4">Contact</h2>
          <ul className="space-y-3 text-sm">
            <li><a href={`tel:${SITE.phone}`} className="flex items-center gap-2 text-foreground/80 hover:text-primary"><Phone className="h-4 w-4" /> {SITE.phoneDisplay}</a></li>
            <li><a href="mailto:info@izhubcleaning.uk" className="flex items-center gap-2 text-foreground/80 hover:text-primary"><Mail className="h-4 w-4" /> info@izhubcleaning.uk</a></li>
            <li><a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-foreground/80 hover:text-primary"><MessageCircle className="h-4 w-4" /> WhatsApp Us</a></li>
            <li className="flex items-start gap-2 text-foreground/80">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
              <address className="not-italic">{SITE.addressLine}</address>
            </li>
            <li className="flex items-start gap-2 text-foreground/80">
              <Clock className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Open {SITE.openingHours}</span>
            </li>
            <li>
              <a
                href={SITE.googleBusinessUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="View IZHUB Cleaning Service on Google Business Profile"
                className="inline-flex items-center gap-2 rounded-full border border-sage/40 bg-sage-soft/60 px-3 py-1.5 text-xs font-medium text-primary hover:bg-sage-soft transition-colors"
              >
                <Star className="h-3.5 w-3.5 fill-primary text-primary" />
                View us on Google
                <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li className="pt-2">
              <div className="flex items-center gap-3">
                <a
                  href={SITE.facebook}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit IZHUB Cleaning Services on Facebook"
                  className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card text-[#1877F2] hover:brightness-110 transition-all"
                >
                  <Facebook className="h-4 w-4 fill-[#1877F2]" />
                </a>
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit IZHUB Cleaning Services on Instagram"
                  className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card transition-all hover:brightness-110"
                >
                  <Instagram
                    className="h-4 w-4"
                    style={{
                      color: "#E1306C",
                      fill: "url(#instagram-gradient)",
                    }}
                  />
                  <svg width="0" height="0" className="absolute">
                    <defs>
                      <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F58529" />
                        <stop offset="40%" stopColor="#DD2A7B" />
                        <stop offset="80%" stopColor="#8134AF" />
                        <stop offset="100%" stopColor="#515BD4" />
                      </linearGradient>
                    </defs>
                  </svg>
                </a>
              </div>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-base mb-4">Quick Links</h2>
          <ul className="space-y-2 text-sm">
            <li><Link to="/services" className="text-foreground/80 hover:text-primary">Services</Link></li>
            <li><Link to="/our-work" className="text-foreground/80 hover:text-primary">Our Work</Link></li>
            <li><Link to="/domestic-cleaning-tidworth" className="text-foreground/80 hover:text-primary">House Cleaning in Tidworth</Link></li>
            <li><Link to="/office-cleaning-tidworth" className="text-foreground/80 hover:text-primary">Office Cleaning in Tidworth</Link></li>
            <li><Link to="/pricing" className="text-foreground/80 hover:text-primary">Pricing</Link></li>
            <li><Link to="/service-areas" className="text-foreground/80 hover:text-primary">Service Areas</Link></li>
            <li><Link to="/about" className="text-foreground/80 hover:text-primary">About</Link></li>
            <li><Link to="/quote" className="text-foreground/80 hover:text-primary">Get a Quote</Link></li>
            <li><Link to="/privacy" className="text-foreground/80 hover:text-primary">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-foreground/80 hover:text-primary">Terms &amp; Conditions</Link></li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-base mb-4">For Cleaners</h2>
          <ul className="space-y-2 text-sm">
            <li><Link to="/join-as-a-cleaner" className="text-foreground/80 hover:text-primary">Join as a Cleaner / Cleaner Portal</Link></li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-base mb-4">Service Areas</h2>
          <ul className="grid grid-cols-2 gap-y-2 text-sm text-foreground/80">
            {areas.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="container-x py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} IZHUB Cleaning Service. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <p>Fully insured · Public liability · UK-based</p>
            <Link to="/admin" className="hover:text-primary transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
